// utils/ai-assets.ts
// Shared utility for locating the ai-assets root directory.
//
// This is NOT a startup path (an earlier comment here claimed it was). Every
// caller is an agent-invokable tool in `tools/flows.ts` / `tools/skills.ts`, so
// the walk runs on a request path inside the long-lived `skaile serve` process,
// once per tool invocation. With `projectDir = /skaile` the upward walk passes
// directly beside the rclone FUSE workspace mount, so a synchronous `statSync`
// here parks the whole event loop when that mount wedges — the failure mode
// behind the 2026-08-07 outages. See issue #454.
import { findAiAssetsRoot as findAiAssetsRootShared } from "@skaile/workspaces/asset-manager/catalog-deployer";

/**
 * Walk up from `startDir` looking for an `ai-assets/` directory.
 *
 * Delegates to the shared implementation in `asset-manager/catalog-deployer`,
 * which is async, deadline-bounded, level-capped, and refuses a candidate that
 * resolves at or under a workspace mount (continuing upward so a legitimate
 * parent still wins).
 *
 * @param startDir - Absolute path to begin the upward search from (typically
 *   the skaile `projectDir`).
 * Note the upward walk is now level-capped rather than running to the
 * filesystem root. Six levels covers every real layout and stops an
 * unbounded climb across mount boundaries.
 *
 * @returns Absolute path to the first usable `ai-assets/` directory found, or
 *   `null` if no match exists within the cap.
 *
 * @docLink packages/workspace-plugin/concepts#workspaceplugin-lifecycle
 */
export function findAiAssetsRoot(startDir: string): Promise<string | null> {
  return findAiAssetsRootShared(startDir);
}
